import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { apiService } from '../../services/api';
import Toast from '../Common/Toast';
import UniversalModal from '../Common/UniversalModal';
import './Support.css';


const MAX_FILES = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const validateFiles = (incoming, current = []) => {
  const merged = [...current];
  for (const file of Array.from(incoming || [])) {
    if (!ALLOWED_TYPES.includes(file.type)) throw new Error(`Файл «${file.name}» должен быть PNG, JPEG или WebP.`);
    if (file.size > MAX_FILE_SIZE) throw new Error(`Файл «${file.name}» превышает 10 МБ.`);
    if (merged.length >= MAX_FILES) throw new Error(`Можно приложить не более ${MAX_FILES} изображений.`);
    merged.push(file);
  }
  return merged;
};

const getBrowserInfo = () => ({
  user_agent: navigator.userAgent,
  platform: navigator.platform || '',
  language: navigator.language || '',
  viewport: { width: window.innerWidth, height: window.innerHeight },
  screen: { width: window.screen?.width, height: window.screen?.height },
});

const captureScreen = async () => {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error('Этот браузер не поддерживает создание снимка вкладки. Загрузите изображение вручную.');
  }
  const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await new Promise((resolve) => setTimeout(resolve, 250));
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.92));
    if (!blob) throw new Error('Не удалось сформировать снимок экрана.');
    return new File([blob], `aot-screenshot-${Date.now()}.png`, { type: 'image/png' });
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }
};

const SupportWidget = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState([]);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({ category: 'bug', priority: 'normal', subject: '', message: '' });
  const pageContext = useMemo(() => ({
    page_url: `${window.location.origin}${window.location.pathname}#${location.pathname}${location.search}`,
    page_title: document.title,
  }), [location.pathname, location.search]);

  useEffect(() => {
    const openHandler = () => setIsOpen(true);
    window.addEventListener('aot-open-support', openHandler);
    return () => window.removeEventListener('aot-open-support', openHandler);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const addFiles = (incoming) => {
    try {
      setFiles((current) => validateFiles(incoming, current));
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    }
  };

  const handlePaste = (event) => {
    const images = Array.from(event.clipboardData?.files || []).filter((file) => file.type.startsWith('image/'));
    if (images.length) {
      event.preventDefault();
      addFiles(images);
    }
  };

  const handleCapture = async () => {
    setIsCapturing(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 250));
      const screenshot = await captureScreen();
      addFiles([screenshot]);
    } catch (error) {
      if (error?.name !== 'NotAllowedError') setToast({ type: 'error', message: error.message });
    } finally {
      setIsCapturing(false);
    }
  };

  const resetAndClose = () => {
    setIsOpen(false);
    setFiles([]);
    setForm({ category: 'bug', priority: 'normal', subject: '', message: '' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      setToast({ type: 'error', message: 'Укажите тему и опишите проблему или предложение.' });
      return;
    }
    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => payload.append(key, value));
    payload.append('page_url', pageContext.page_url);
    payload.append('page_title', pageContext.page_title);
    payload.append('browser_info', JSON.stringify(getBrowserInfo()));
    files.forEach((file) => payload.append('attachments', file));
    setSubmitting(true);
    try {
      const response = await apiService.createSupportRequest(payload);
      resetAndClose();
      window.dispatchEvent(new Event('aot-support-updated'));
      setToast({ type: 'success', message: `Обращение #${response.data.id} отправлено администратору.` });
    } catch (error) {
      const data = error.response?.data;
      const message = data?.detail || data?.attachments?.[0] || data?.message?.[0] || error.message;
      setToast({ type: 'error', message: `Не удалось отправить обращение: ${message}` });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button type="button" className="support-fab" onClick={() => setIsOpen(true)} title="Сообщить о проблеме" aria-label="Открыть форму технической поддержки">
        <span className="support-fab__icon" aria-hidden="true">?</span>
        <span className="support-fab__text">Поддержка</span>
      </button>

      <UniversalModal
        isOpen={isOpen && !isCapturing}
        onClose={() => setIsOpen(false)}
        title="Обращение в техподдержку"
        modalClassName="support-create-modal"
      >
        <form className="support-form" onSubmit={handleSubmit} onPaste={handlePaste}>
          <div className="support-form__intro">
            Опишите, что работает неправильно или что можно улучшить. Администратор увидит страницу, с которой отправлено обращение.
          </div>
          <div className="support-form__grid">
            <label>
              <span>Тип обращения</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="bug">Что-то работает неправильно</option>
                <option value="improvement">Предложение по улучшению</option>
                <option value="question">Вопрос</option>
                <option value="comment">Комментарий</option>
              </select>
            </label>
            <label>
              <span>Приоритет</span>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option value="normal">Обычный</option>
                <option value="high">Высокий</option>
                <option value="critical">Критический</option>
              </select>
            </label>
          </div>
          <label>
            <span>Тема</span>
            <input maxLength={200} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Коротко опишите вопрос" autoFocus />
          </label>
          <label>
            <span>Описание</span>
            <textarea maxLength={10000} rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Что произошло, что вы ожидали и как повторить проблему?" />
          </label>

          <div
            className="support-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
          >
            <strong>Скриншоты</strong>
            <span>Перетащите изображение сюда или вставьте из буфера через Ctrl/Cmd + V</span>
            <div className="support-dropzone__actions">
              <button type="button" className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>Выбрать файл</button>
              <button type="button" className="btn btn-secondary" onClick={handleCapture}>Сделать снимок экрана</button>
            </div>
            <small>Перед отправкой убедитесь, что на снимке нет паролей и других конфиденциальных данных.</small>
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          </div>
          {files.length > 0 && (
            <div className="support-file-list">
              {files.map((file, index) => (
                <div className="support-file-chip" key={`${file.name}-${file.size}-${index}`}>
                  <span>{file.name}</span>
                  <button type="button" onClick={() => setFiles(files.filter((_, fileIndex) => fileIndex !== index))} aria-label={`Удалить ${file.name}`}>&times;</button>
                </div>
              ))}
            </div>
          )}
          <div className="support-context-note">Страница: {location.pathname}</div>
          <div className="support-form__actions">
            <button type="button" className="support-link-button" onClick={() => { setIsOpen(false); navigate('/support'); }}>Мои обращения</button>
            <button type="button" className="btn btn-secondary" onClick={() => setIsOpen(false)}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Отправка...' : 'Отправить'}</button>
          </div>
        </form>
      </UniversalModal>
      <Toast toast={toast} />
    </>
  );
};

export default SupportWidget;
